import React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react";
import type { Column } from "@tanstack/react-table";
import type { FilterType, FilterValue } from "./types";
import { SelectFilter, DateRangeFilter } from "./filters/FilterComponents";

interface FilterConfigDialogProps<T> {
  column: Column<T>;
  filterType: FilterType;
  columnLabel: string;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  onClear: () => void;
  trigger?: React.ReactNode;
  className?: string;
  selectOptions?: Array<{ value: string; label: string }>;
}

/**
 * Popover dialog for configuring filter values
 * Reuses the existing filter components from the original implementation
 */
export function FilterConfigDialog<T>({
  column,
  filterType,
  columnLabel,
  value,
  onChange,
  onClear,
  trigger,
  className,
  selectOptions,
}: FilterConfigDialogProps<T>) {
  const { i18n } = useLingui();
  const [open, setOpen] = React.useState(false);

  const facetedValues = column.getFacetedUniqueValues();
  const minMaxValues = column.getFacetedMinMaxValues();

  const handleFilterChange = (newValue: FilterValue) => {
    // Check if the filter value is meaningless (empty) and should be removed entirely
    const isEmpty = (() => {
      if (!newValue) return true;

      // For array values (select filters)
      if (Array.isArray(newValue)) {
        return newValue.length === 0;
      }

      // For object values (date ranges)
      if (
        typeof newValue === "object" &&
        newValue !== null &&
        !Array.isArray(newValue)
      ) {
        const obj = newValue as Record<string, unknown>;
        // Check if all properties are undefined/null or if object is empty
        return (
          Object.keys(obj).length === 0 ||
          Object.values(obj).every((val) => val == null)
        );
      }

      return false;
    })();

    if (isEmpty) {
      // If the value is empty, remove the filter completely
      onClear();
    } else {
      // Otherwise, update the filter value
      onChange(newValue);
    }
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  // Render the appropriate filter component based on type
  const renderFilterComponent = () => {
    const commonProps = {
      value,
      onChange: handleFilterChange,
      onClear: handleClear,
      facetedValues,
      minMaxValues,
      selectOptions,
      columnLabel,
    };

    switch (filterType) {
      case "select":
        return <SelectFilter {...commonProps} />;
      case "date":
        return <DateRangeFilter {...commonProps} />;
      default:
        // Handle unknown filter types explicitly
        return (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <p>
              {i18n._({
                id: "table.filter.unsupported",
                message: "This filter cannot be set here.",
              })}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              className="mt-3"
            >
              {i18n._({ id: "kitchen.filters.removeFilter", message: "Remove filter" })}
            </Button>
          </div>
        );
    }
  };

  const defaultTrigger = (
    <Button
      variant="outline"
      size="sm"
      className={cn("h-8 w-8 p-0", className)}
      aria-label={i18n._({
        id: "table.filter.configure",
        message: "Configure the {column} filter",
        values: { column: columnLabel },
      })}
    >
      <Settings className="h-4 w-4" aria-hidden="true" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger || defaultTrigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="bottom">
        <div className="p-3 border-b">
          <h4 className="font-medium text-sm">Filter: {columnLabel}</h4>
        </div>
        {renderFilterComponent()}
      </PopoverContent>
    </Popover>
  );
}
